'use client';

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { splashScreenService } from '@/lib/splashScreenService';

interface NavigationLoadingContextType {
  isNavigating: boolean;
  targetPath: string | null;
  setNavigating: (path: string) => void;
  clearNavigating: () => void;
}

const NavigationLoadingContext = createContext<NavigationLoadingContextType>({
  isNavigating: false,
  targetPath: null,
  setNavigating: () => {},
  clearNavigating: () => {},
});

export function useNavigationLoading() {
  return useContext(NavigationLoadingContext);
}

interface NavigationLoadingProviderProps {
  children: React.ReactNode;
}

export function NavigationLoadingProvider({ children }: NavigationLoadingProviderProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const previousPathRef = useRef<string>('');
  const pathname = usePathname();

  // Use pathname only - avoid useSearchParams to prevent Suspense issues
  const currentPath = pathname;

  const setNavigatingState = (path: string) => {
    if (typeof window !== 'undefined') {
      const nextPathname = resolveNavigationPathname(path);
      const currentPathname = normalizeNavigationPathname(window.location.pathname);

      if (nextPathname && nextPathname === currentPathname) {
        return;
      }
    }

    setIsNavigating(true);
    setTargetPath(path);
    // Show unified splash screen during navigation
    splashScreenService.show({
      message: `Navigating to ${getPageName(path)}`,
      subMessage: 'Please wait...'
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const isInsideAppShell =
      currentPath === '/client-zone' || currentPath?.startsWith('/client-zone/') === true;

    const shouldHandleDashboardNavigation =
      currentPath === '/client-zone/dashboard' ||
      currentPath === '/client-zone/portal' ||
      currentPath === '/client-zone' ||
      currentPath?.startsWith('/client-zone/modules/') ||
      currentPath?.startsWith('/client-zone/admin-hub');

    // Public pages also need click → loading feedback; inside the app shell we
    // only cover the module/admin routes handled below.
    if (!shouldHandleDashboardNavigation && isInsideAppShell) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      if (anchor.target && anchor.target !== '_self') {
        return;
      }

      if (anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      let nextUrl: URL;
      try {
        nextUrl = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (nextUrl.origin !== window.location.origin) {
        return;
      }

      const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

      if (nextPath === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        return;
      }

      const isModuleOrAdminRoute = nextUrl.pathname.startsWith('/client-zone/modules/') || nextUrl.pathname.startsWith('/client-zone/admin-hub');
      const isPublicPageNavigation = !isInsideAppShell;

      if (!isPublicPageNavigation && !(shouldHandleDashboardNavigation && isModuleOrAdminRoute)) {
        return;
      }

      // Same-pathname navigations (hash links, query-only changes) never remount
      // the page — there is nothing to wait on, and the clear-on-pathname-change
      // effect would never fire.
      if (normalizeNavigationPathname(nextUrl.pathname) === normalizeNavigationPathname(window.location.pathname)) {
        return;
      }

      setNavigatingState(nextPath);
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [currentPath]);

  // Clear navigation state when route actually changes
  useEffect(() => {
    const previousPath = previousPathRef.current;

    // Only clear if we were navigating AND the path has actually changed
    if (isNavigating && previousPath && currentPath !== previousPath) {
      // Add a small delay to ensure the new page has started rendering
      // This prevents the flash of loading screens on the new page
      const timer = setTimeout(() => {
        setIsNavigating(false);
        setTargetPath(null);
        // Hide the splash screen
        splashScreenService.hide();
      }, 500); // 500ms delay to allow page components to mount

      return () => clearTimeout(timer);
    }

    previousPathRef.current = currentPath;
  }, [currentPath, isNavigating]);

  // Safety net: the click listener runs in the capture phase, before any
  // handler that might preventDefault() the navigation (or where navigation is
  // cancelled by other means). Never leave the splash up indefinitely.
  useEffect(() => {
    if (!isNavigating) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsNavigating(false);
      setTargetPath(null);
      splashScreenService.hide();
    }, 20000);

    return () => clearTimeout(timeout);
  }, [isNavigating]);

  const clearNavigating = () => {
    setIsNavigating(false);
    setTargetPath(null);
    splashScreenService.hide();
  };

  return (
    <NavigationLoadingContext.Provider
      value={{
        isNavigating,
        targetPath,
        setNavigating: setNavigatingState,
        clearNavigating,
      }}
    >
      {children}
      {/* Splash screen is handled by SplashScreenProvider in layout */}
    </NavigationLoadingContext.Provider>
  );
}

function normalizeNavigationPathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

function resolveNavigationPathname(path: string): string | null {
  if (!path || (!path.startsWith('/') && !/^https?:\/\//i.test(path))) {
    return null;
  }

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid';
    const resolved = new URL(path, base);
    return normalizeNavigationPathname(resolved.pathname);
  } catch {
    return null;
  }
}

// Helper function to get friendly page names
function getPageName(path: string): string {
  const resolvedPath = resolveNavigationPathname(path) ?? path.split(/[?#]/)[0] ?? path;

  if (resolvedPath === '/') return 'Home';
  if (resolvedPath === '/client-zone/dashboard' || resolvedPath === '/client-zone/portal') return 'Dashboard';
  if (resolvedPath.startsWith('/client-zone/admin-hub')) return 'Admin Hub';
  if (resolvedPath.startsWith('/client-zone/modules/')) {
    const moduleName = resolvedPath.split('/').pop() || '';
    return moduleName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  if (resolvedPath.startsWith('/client-zone/')) {
    const pageName = resolvedPath.replace('/client-zone/', '');
    return pageName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  // Default: capitalize and format path
  return resolvedPath
    .split('/')
    .filter(Boolean)
    .pop()
    ?.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') || 'Page';
}
